import { notifyError } from "@/lib/notify-error";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { api } from "@constructionautomation/backend";
import { useMutation } from "convex/react";
import { toast } from "sonner";

import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { DetectionList } from "@/features/document-intelligence/document-tag-detection-list";
import {
  DocumentTagContextMenu,
  DocumentTagEditorPanel,
  type DocumentTagEditorSelectionAction,
} from "@/features/document-intelligence/document-tag-editor-panel";
import { useDocumentViewport } from "@/features/document-viewport/context";
import { clamp } from "@/features/rev-collab/rev-collab-session-helpers";
import { APP_HOTKEY_SCOPES, useAppHotkeys } from "@/hooks/useAppHotkeys";
import { useOutsidePointerDown } from "@/hooks/useOutsidePointerDown";
import { cn } from "@/lib/utils";
import {
  getDetectionMatchText,
  getOptionMatchText,
  normalizeTagMatchText,
  type DocumentTagOption,
  type DocumentTagPlacementMode,
  type TagDetectionClassification,
} from "@/viewport-tools/tags";
import { useCanEdit } from "@/lib/capabilities";

interface BaseDetectionRecord {
  _id: Id<"documentIntelligenceDetections">;
  rawText: string;
  normalizedText?: string;
  classification: TagDetectionClassification;
  bbox: { x: number; y: number; width: number; height: number };
  createdByUserId?: Id<"users">;
  archivedAt?: number;
}
type Classification = BaseDetectionRecord["classification"];

interface StyleResolver {
  color: string;
  fillClassName?: string;
  fillColor?: string;
  badgeText?: string;
}

interface ClipboardTag {
  sourceDetectionId?: Id<"documentIntelligenceDetections">;
  rawText: string;
  classification: Classification;
  bbox: BaseDetectionRecord["bbox"];
}
type DuplicatePlacement = "cursor" | "offset";

interface SpacePanStart {
  clientX: number;
  clientY: number;
  panX: number;
  panY: number;
}

const PASTE_OFFSET = 0.02;
const DEFAULT_TAG_BBOX = { x: 0, y: 0, width: 0.05, height: 0.03 };

function isEditableElement(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

function normalizeRect(
  pointA: { x: number; y: number },
  pointB: { x: number; y: number }
) {
  const x = Math.min(pointA.x, pointB.x);
  const y = Math.min(pointA.y, pointB.y);
  return {
    x,
    y,
    width: Math.abs(pointA.x - pointB.x),
    height: Math.abs(pointA.y - pointB.y),
  };
}

function offsetRect(rect: BaseDetectionRecord["bbox"]) {
  return {
    x: clamp(rect.x + PASTE_OFFSET, 0, 1 - rect.width),
    y: clamp(rect.y + PASTE_OFFSET, 0, 1 - rect.height),
    width: rect.width,
    height: rect.height,
  };
}

function centerRectAtPoint(
  rect: BaseDetectionRecord["bbox"],
  point: { x: number; y: number }
) {
  return {
    x: clamp(point.x - rect.width / 2, 0, 1 - rect.width),
    y: clamp(point.y - rect.height / 2, 0, 1 - rect.height),
    width: rect.width,
    height: rect.height,
  };
}

function getAbsoluteRectStyle(rect: { x: number; y: number; width: number; height: number }) {
  const round = (value: number) => Math.round(value * 1000) / 1000;
  return {
    left: round(rect.x),
    top: round(rect.y),
    width: round(rect.width),
    height: round(rect.height),
  };
}

function isPlacementPreviewSuppressedTarget(target: EventTarget | null) {
  return target instanceof HTMLElement && Boolean(
    target.closest("[data-document-tag-editor-ui], [data-viewport-tools-ui], [role='dialog']")
  );
}

function rectsIntersect(
  a: { left: number; top: number; right: number; bottom: number },
  b: { left: number; top: number; right: number; bottom: number }
) {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function isPlacementPreviewOverSuppressedUi(rect: { left: number; top: number; width: number; height: number }) {
  const previewRect = {
    left: rect.left,
    top: rect.top,
    right: rect.left + rect.width,
    bottom: rect.top + rect.height,
  };

  return Array.from(
    document.querySelectorAll<HTMLElement>(
      "[data-document-tag-editor-ui], [data-viewport-tools-ui], [role='dialog']"
    )
  ).some((element) => {
    const bounds = element.getBoundingClientRect();
    if (bounds.width === 0 || bounds.height === 0) {
      return false;
    }
    return rectsIntersect(previewRect, bounds);
  });
}

function getOptionTemplate<TDetection extends BaseDetectionRecord>(args: {
  option: DocumentTagOption;
  allDetections: TDetection[];
}) {
  const matchingDetection = args.allDetections.find(
    (detection) =>
      detection.rawText.trim() === args.option.rawText.trim() ||
      ("normalizedText" in detection &&
        typeof detection.normalizedText === "string" &&
        args.option.normalizedText &&
        detection.normalizedText === args.option.normalizedText)
  );

  return {
    rawText: args.option.rawText,
    classification:
      args.option.classification ?? matchingDetection?.classification ?? "unknown",
    bbox: args.option.bbox ?? matchingDetection?.bbox ?? DEFAULT_TAG_BBOX,
  };
}

function findTagOptionForDetection(
  options: DocumentTagOption[],
  detection: BaseDetectionRecord | null
) {
  if (!detection) {
    return null;
  }

  return (
    options.find((option) => option.rawText.trim() === detection.rawText.trim()) ?? null
  );
}

function getSelectionUpdate(current: string[], detectionId: string, additive: boolean) {
  if (!additive) {
    return [detectionId];
  }
  return current.includes(detectionId)
    ? current.filter((id) => id !== detectionId)
    : [...current, detectionId];
}

function getDragDeltaBounds(rects: Array<BaseDetectionRecord["bbox"]>) {
  return rects.reduce(
    (bounds, rect) => ({
      minX: Math.max(bounds.minX, -rect.x),
      maxX: Math.min(bounds.maxX, 1 - rect.width - rect.x),
      minY: Math.max(bounds.minY, -rect.y),
      maxY: Math.min(bounds.maxY, 1 - rect.height - rect.y),
    }),
    { minX: -Infinity, maxX: Infinity, minY: -Infinity, maxY: Infinity }
  );
}

function shouldRenderTagEditorPanel({
  hidePanelWhenNotEditing,
  editMode,
  showPanelOnlyWhenSelection,
  hasPanelSelection,
}: {
  hidePanelWhenNotEditing: boolean;
  editMode: boolean;
  showPanelOnlyWhenSelection: boolean;
  hasPanelSelection: boolean;
}) {
  if (hidePanelWhenNotEditing && !editMode && !hasPanelSelection) {
    return false;
  }
  if (showPanelOnlyWhenSelection && !hasPanelSelection) {
    return false;
  }
  return true;
}

function startCreateRect(args: {
  allowCreate: boolean;
  editMode: boolean;
  createMode: boolean;
  event: React.MouseEvent<HTMLDivElement>;
  getPagePointFromClientPosition: (clientX: number, clientY: number) => { x: number; y: number } | null;
  setDrawingRect: React.Dispatch<
    React.SetStateAction<{
      startPoint: { x: number; y: number };
      currentPoint: { x: number; y: number };
    } | null>
  >;
}) {
  if (!args.allowCreate || !args.editMode || !args.createMode) {
    return;
  }
  const pagePoint = args.getPagePointFromClientPosition(args.event.clientX, args.event.clientY);
  if (!pagePoint) {
    return;
  }
  args.setDrawingRect({
    startPoint: pagePoint,
    currentPoint: pagePoint,
  });
}

function useDocumentTagHotkeys(args: {
  editMode: boolean;
  hasSelectedDetection: boolean;
  onCopy: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
}) {
  useAppHotkeys(
    "mod+c",
    () => {
      args.onCopy();
    },
    {
      enabled: args.editMode && args.hasSelectedDetection,
      preventDefault: true,
      scope: APP_HOTKEY_SCOPES.documentTagEditor,
    },
    [args.editMode, args.hasSelectedDetection, args.onCopy]
  );

  useAppHotkeys(
    "mod+v",
    () => {
      args.onPaste();
    },
    {
      enabled: args.editMode,
      preventDefault: true,
      scope: APP_HOTKEY_SCOPES.documentTagEditor,
    },
    [args.editMode, args.onPaste]
  );

  useAppHotkeys(
    "mod+d",
    () => {
      args.onDuplicate();
    },
    {
      enabled: args.editMode && args.hasSelectedDetection,
      preventDefault: true,
      scope: APP_HOTKEY_SCOPES.documentTagEditor,
    },
    [args.editMode, args.hasSelectedDetection, args.onDuplicate]
  );
}

function useHighlightedMarkerPosition<TDetection extends BaseDetectionRecord>(args: {
  allDetections: TDetection[];
  highlightedDetectionId?: string | null;
  pageRectToScreenRect: (rect: BaseDetectionRecord["bbox"]) => {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}) {
  const highlightedDetection = useMemo(
    () =>
      args.highlightedDetectionId
        ? args.allDetections.find(
            (detection) => String(detection._id) === args.highlightedDetectionId
          ) ?? null
        : null,
    [args.allDetections, args.highlightedDetectionId]
  );

  return useMemo(() => {
    if (!highlightedDetection) {
      return null;
    }
    const rect = args.pageRectToScreenRect(highlightedDetection.bbox);
    return {
      x: rect.x + rect.width / 2,
      y: rect.y + rect.height / 2,
      label: highlightedDetection.rawText,
    };
  }, [highlightedDetection, args.pageRectToScreenRect]);
}

interface DocumentTagEditorOverlayProps<TDetection extends BaseDetectionRecord> {
  revisionId: Id<"projectDocumentRevisions">;
  activeDetections: TDetection[];
  archivedDetections: TDetection[];
  getStyle: (detection: TDetection) => StyleResolver;
  showInlineLabel?: boolean;
  secondaryLabel?: (detection: TDetection) => string | undefined;
  panelClassName?: string;
  highlightedDetectionId?: string | null;
  editMode?: boolean;
  onEditModeChange?: (value: boolean) => void;
  showEditToggle?: boolean;
  hidePanelWhenNotEditing?: boolean;
  showPanelOnlyWhenSelection?: boolean;
  allowCreate?: boolean;
  tagOptions?: DocumentTagOption[];
  selectedAddTagOptionId?: string | null;
  placementMode?: DocumentTagPlacementMode;
  onAction?: () => void | Promise<void>;
  getSelectedDetectionAction?: (
    detection: TDetection
  ) => DocumentTagEditorSelectionAction | null;
  selectedDetectionIds?: string[];
  onSelectedDetectionIdsChange?: (detectionIds: string[]) => void;
}

function selectionsMatch(current: string[], next: string[]) {
  return (
    current.length === next.length &&
    current.every((detectionId, index) => detectionId === next[index])
  );
}

function useTagOverlaySpacePan(args: {
  editMode: boolean;
  pan: { x: number; y: number };
  setPan: (pan: { x: number; y: number }) => void;
  onPanStart: () => void;
}) {
  const { editMode, onPanStart, pan, setPan } = args;
  const [spacePanStart, setSpacePanStart] = useState<SpacePanStart | null>(null);
  const [isSpacePanActive, setIsSpacePanActive] = useState(false);
  const isSpacePressedRef = useRef(false);
  const suppressNextClickRef = useRef(false);
  const suppressClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSuppressedClickTimer = useCallback(() => {
    if (suppressClearTimerRef.current === null) {
      return;
    }
    clearTimeout(suppressClearTimerRef.current);
    suppressClearTimerRef.current = null;
  }, []);

  const clearSuppressedClick = useCallback(() => {
    clearSuppressedClickTimer();
    suppressNextClickRef.current = false;
  }, [clearSuppressedClickTimer]);

  const scheduleClearSuppressedClick = useCallback(() => {
    clearSuppressedClickTimer();
    suppressClearTimerRef.current = setTimeout(() => {
      suppressClearTimerRef.current = null;
      suppressNextClickRef.current = false;
    }, 0);
  }, [clearSuppressedClickTimer]);

  useEffect(() => clearSuppressedClickTimer, [clearSuppressedClickTimer]);

  useEffect(() => {
    if (!editMode) {
      isSpacePressedRef.current = false;
      clearSuppressedClick();
      setIsSpacePanActive(false);
      setSpacePanStart(null);
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== " " || isEditableElement(event.target)) {
        return;
      }
      event.preventDefault();
      isSpacePressedRef.current = true;
      setIsSpacePanActive(true);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key !== " ") {
        return;
      }
      isSpacePressedRef.current = false;
      setIsSpacePanActive(false);
      setSpacePanStart(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [clearSuppressedClick, editMode]);

  useEffect(() => {
    if (!spacePanStart) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      setPan({
        x: spacePanStart.panX + (event.clientX - spacePanStart.clientX),
        y: spacePanStart.panY + (event.clientY - spacePanStart.clientY),
      });
    };

    const handleMouseUp = () => {
      setSpacePanStart(null);
      scheduleClearSuppressedClick();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [scheduleClearSuppressedClick, setPan, spacePanStart]);

  const startSpacePan = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!isSpacePressedRef.current) {
        return;
      }
      event.preventDefault();
      clearSuppressedClickTimer();
      suppressNextClickRef.current = true;
      onPanStart();
      setSpacePanStart({
        clientX: event.clientX,
        clientY: event.clientY,
        panX: pan.x,
        panY: pan.y,
      });
    },
    [clearSuppressedClickTimer, onPanStart, pan.x, pan.y]
  );

  const consumeSuppressedClick = useCallback(() => {
    if (!suppressNextClickRef.current) {
      return false;
    }
    clearSuppressedClick();
    return true;
  }, [clearSuppressedClick]);

  const resetSpacePan = useCallback(() => {
    setSpacePanStart(null);
    setIsSpacePanActive(false);
    clearSuppressedClick();
  }, [clearSuppressedClick]);

  return {
    isSpacePanActive,
    spacePanStart,
    startSpacePan,
    resetSpacePan,
    consumeSuppressedClick,
  };
}

function useBulkTagRetag<TDetection extends BaseDetectionRecord>(args: {
  revisionId: Id<"projectDocumentRevisions">;
  selectedDetection: TDetection | null;
  tagOptions: DocumentTagOption[];
  allDetections: TDetection[];
  setPendingId: (pendingId: string | null) => void;
  onRetagged: () => void;
  onAction?: () => void | Promise<void>;
}) {
  const { allDetections, onAction, onRetagged, revisionId, selectedDetection, setPendingId, tagOptions } = args;
  const updateMatchingTagsInRevision = useMutation(
    api.documentIntelligence.tags.updateMatchingTagsInRevision
  );
  const [bulkTargetOptionId, setBulkTargetOptionId] = useState<string | null>(null);
  const [showBulkRetagControls, setShowBulkRetagControls] = useState(false);
  const [pendingBulkRetag, setPendingBulkRetag] = useState<{
    source: TDetection;
    target: DocumentTagOption;
  } | null>(null);
  const recordActionSafely = useCallback(async () => {
    try {
      await onAction?.();
    } catch (error) {
      console.error("Action log failed (non-fatal):", error);
    }
  }, [onAction]);

  useEffect(() => {
    if (!selectedDetection) {
      setBulkTargetOptionId(null);
      setShowBulkRetagControls(false);
      return;
    }

    setBulkTargetOptionId((current) => {
      const selectedMatchText = normalizeTagMatchText(getDetectionMatchText(selectedDetection));
      const availableOptions = tagOptions.filter(
        (tagOption) => getOptionMatchText(tagOption) !== selectedMatchText
      );
      const option = tagOptions.find((tagOption) => tagOption.id === current);
      return option && getOptionMatchText(option) !== selectedMatchText
        ? current
        : availableOptions[0]?.id ?? null;
    });
  }, [selectedDetection, tagOptions]);

  const requestBulkRetag = useCallback(() => {
    if (!selectedDetection || !bulkTargetOptionId) {
      return;
    }
    const target = tagOptions.find((option) => option.id === bulkTargetOptionId);
    if (!target) {
      return;
    }
    setPendingBulkRetag({ source: selectedDetection, target });
  }, [bulkTargetOptionId, selectedDetection, tagOptions]);

  const confirmBulkRetag = useCallback(async () => {
    if (!pendingBulkRetag) {
      return;
    }
    const template = getOptionTemplate({
      option: pendingBulkRetag.target,
      allDetections,
    });
    try {
      setPendingId("bulk-retag");
      const result = await updateMatchingTagsInRevision({
        revisionId,
        sourceNormalizedText: getDetectionMatchText(pendingBulkRetag.source),
        targetRawText: template.rawText,
        targetClassification: template.classification,
      });
      setPendingBulkRetag(null);
      onRetagged();
      await recordActionSafely();
      toast.success(`Changed ${result.updatedCount} tags to ${template.rawText}`);
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to change matching tags");
      // Rethrow so the confirmation dialog stays open for a retry.
      throw error;
    } finally {
      setPendingId(null);
    }
  }, [allDetections, onRetagged, pendingBulkRetag, recordActionSafely, revisionId, setPendingId, updateMatchingTagsInRevision]);

  const toggleBulkRetagControls = useCallback(() => {
    setShowBulkRetagControls((current) => !current);
  }, []);

  const resetBulkRetag = useCallback(() => {
    setBulkTargetOptionId(null);
    setShowBulkRetagControls(false);
    setPendingBulkRetag(null);
  }, []);

  return {
    bulkTargetOptionId,
    showBulkRetagControls,
    pendingBulkRetag,
    setBulkTargetOptionId,
    setPendingBulkRetag,
    toggleBulkRetagControls,
    resetBulkRetag,
    requestBulkRetag,
    confirmBulkRetag,
  };
}

// eslint-disable-next-line complexity -- This coordinator still owns tag placement, drag, clipboard, and mutation orchestration; UI rendering lives in child components.
export function DocumentTagEditorOverlay<TDetection extends BaseDetectionRecord>({
  revisionId,
  activeDetections,
  archivedDetections,
  getStyle,
  showInlineLabel = false,
  secondaryLabel,
  panelClassName,
  highlightedDetectionId,
  editMode: controlledEditMode,
  onEditModeChange,
  showEditToggle = true,
  hidePanelWhenNotEditing = false,
  showPanelOnlyWhenSelection = false,
  allowCreate = true,
  tagOptions = [],
  selectedAddTagOptionId = null,
  placementMode: requestedPlacementMode = null,
  onAction,
  getSelectedDetectionAction,
  selectedDetectionIds: controlledSelectedDetectionIds,
  onSelectedDetectionIdsChange,
}: DocumentTagEditorOverlayProps<TDetection>) {
  const viewport = useDocumentViewport();
  // Tag edits are project writes: viewers who can't edit (reviewers) get selection and highlight
  // only, with no editor panel, context menu, placement, drag, or clipboard hotkeys.
  const canEdit = useCanEdit();
  const placementMode = canEdit ? requestedPlacementMode : null;
  const createManualTag = useMutation(api.documentIntelligence.tags.createManualTag);
  const duplicateTag = useMutation(api.documentIntelligence.tags.duplicateTag);
  const updateTag = useMutation(api.documentIntelligence.tags.updateTag);
  const moveTag = useMutation(api.documentIntelligence.tags.moveTag);
  const archiveTag = useMutation(api.documentIntelligence.tags.archiveTag);
  const restoreTag = useMutation(api.documentIntelligence.tags.restoreTag);
  const deleteTag = useMutation(api.documentIntelligence.tags.deleteTag);

  const [internalEditMode, setInternalEditMode] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [internalSelectedDetectionIds, setInternalSelectedDetectionIds] = useState<string[]>([]);
  const [draftText, setDraftText] = useState("");
  const [draftClassification, setDraftClassification] = useState<Classification>("unknown");
  const [draftTagOptionId, setDraftTagOptionId] = useState<string | null>(null);
  const [createDraftRect, setCreateDraftRect] = useState<BaseDetectionRecord["bbox"] | null>(null);
  const [drawingRect, setDrawingRect] = useState<{
    startPoint: { x: number; y: number };
    currentPoint: { x: number; y: number };
  } | null>(null);
  const [dragState, setDragState] = useState<{
    detectionId: Id<"documentIntelligenceDetections">;
    pointerOffsetX: number;
    pointerOffsetY: number;
    width: number;
    height: number;
    initialRects: Record<string, BaseDetectionRecord["bbox"]>;
  } | null>(null);
  const [dragPreviewRects, setDragPreviewRects] = useState<Record<string, BaseDetectionRecord["bbox"]>>({});
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [placementPreviewPoint, setPlacementPreviewPoint] = useState<{ x: number; y: number } | null>(null);
  const [duplicateSourceDetectionId, setDuplicateSourceDetectionId] = useState<string | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const clipboardRef = useRef<ClipboardTag | null>(null);
  const lastPointerPagePointRef = useRef<{ x: number; y: number } | null>(null);
  const placementPreviewBboxRef = useRef<BaseDetectionRecord["bbox"]>(DEFAULT_TAG_BBOX);
  const overlayBoundsRef = useRef<HTMLDivElement | null>(null);
  const selectedDetectionIds =
    controlledSelectedDetectionIds ?? internalSelectedDetectionIds;
  const controlledSelectedDetectionIdsRef = useRef(controlledSelectedDetectionIds);
  const onSelectedDetectionIdsChangeRef = useRef(onSelectedDetectionIdsChange);
  controlledSelectedDetectionIdsRef.current = controlledSelectedDetectionIds;
  onSelectedDetectionIdsChangeRef.current = onSelectedDetectionIdsChange;
  const setSelectedDetectionIds = useCallback<
    React.Dispatch<React.SetStateAction<string[]>>
  >(
    (value) => {
      const controlledSelection = controlledSelectedDetectionIdsRef.current;
      if (controlledSelection !== undefined) {
        const nextValue =
          typeof value === "function" ? value(controlledSelection) : value;
        if (selectionsMatch(controlledSelection, nextValue)) {
          return;
        }
        controlledSelectedDetectionIdsRef.current = nextValue;
        onSelectedDetectionIdsChangeRef.current?.(nextValue);
        return;
      }
      setInternalSelectedDetectionIds((current) => {
        const nextValue = typeof value === "function" ? value(current) : value;
        if (selectionsMatch(current, nextValue)) {
          return current;
        }
        onSelectedDetectionIdsChangeRef.current?.(nextValue);
        return nextValue;
      });
    },
    []
  );

  const allDetections = useMemo(
    () => [...activeDetections, ...archivedDetections],
    [activeDetections, archivedDetections]
  );
  const editMode = canEdit && (controlledEditMode ?? internalEditMode);
  const archivedDetectionIds = useMemo(
    () => new Set(archivedDetections.map((detection) => String(detection._id))),
    [archivedDetections]
  );
  const selectedDetectionId = selectedDetectionIds.at(-1) ?? null;
  const selectedDetectionIdSet = useMemo(
    () => new Set(selectedDetectionIds),
    [selectedDetectionIds]
  );
  const selectedDetection =
    allDetections.find((detection) => String(detection._id) === selectedDetectionId) ?? null;
  const selectedDetections = useMemo(
    () =>
      selectedDetectionIds
        .map((detectionId) => allDetections.find((detection) => String(detection._id) === detectionId) ?? null)
        .filter((detection): detection is TDetection => detection !== null),
    [allDetections, selectedDetectionIds]
  );
  const isArchivedDetection = useCallback(
    (detection: TDetection) => detection.archivedAt !== undefined || archivedDetectionIds.has(String(detection._id)),
    [archivedDetectionIds]
  );
  const selectedActiveDetections = selectedDetections.filter(
    (detection) => !isArchivedDetection(detection)
  );
  const selectedArchivedDetections = selectedDetections.filter(isArchivedDetection);
  const selectedDeletableDetections = selectedDetections.filter(
    (detection) => detection.createdByUserId !== undefined
  );
  const hasPlacementPreviewTemplate =
    placementMode === "add"
      ? selectedAddTagOptionId
        ? tagOptions.some((option) => option.id === selectedAddTagOptionId)
        : tagOptions.length > 0
      : placementMode === "duplicate" && duplicateSourceDetectionId !== null;
  const isCreateDraftActive = createDraftRect !== null;
  const selectedDetectionWasCreatedByUser = selectedDetection?.createdByUserId !== undefined;
  const handleBulkRetagged = useCallback(() => {
    setSelectedDetectionIds([]);
    setCreateDraftRect(null);
  }, []);
  const recordActionSafely = useCallback(async () => {
    try {
      await onAction?.();
    } catch (error) {
      console.error("Action log failed (non-fatal):", error);
    }
  }, [onAction]);
  const {
    bulkTargetOptionId,
    showBulkRetagControls,
    pendingBulkRetag,
    setBulkTargetOptionId,
    setPendingBulkRetag,
    toggleBulkRetagControls,
    resetBulkRetag,
    requestBulkRetag,
    confirmBulkRetag,
  } = useBulkTagRetag({
    revisionId,
    selectedDetection,
    tagOptions,
    allDetections,
    setPendingId,
    onRetagged: handleBulkRetagged,
    onAction: recordActionSafely,
  });

  const setEditMode = (value: boolean | ((current: boolean) => boolean)) => {
    const nextValue = typeof value === "function" ? value(editMode) : value;
    if (controlledEditMode === undefined) {
      setInternalEditMode(nextValue);
    }
    onEditModeChange?.(nextValue);
  };

  const getPagePointFromClientPosition = useCallback(
    (clientX: number, clientY: number) => {
      const bounds = overlayBoundsRef.current?.getBoundingClientRect();
      if (!bounds) {
        return null;
      }
      return viewport.transforms.screenToPagePoint({
        x: clientX - bounds.left,
        y: clientY - bounds.top,
      });
    },
    [viewport.transforms]
  );

  const handleSpacePanStart = useCallback(() => {
    setDrawingRect(null);
    setDragState(null);
    setDragPreviewRects({});
  }, []);
  const {
    isSpacePanActive,
    spacePanStart,
    startSpacePan,
    resetSpacePan,
    consumeSuppressedClick,
  } = useTagOverlaySpacePan({
    editMode,
    pan: viewport.camera.pan,
    setPan: viewport.setPan,
    onPanStart: handleSpacePanStart,
  });

  useEffect(() => {
    setSelectedDetectionIds([]);
    setCreateDraftRect(null);
    setDrawingRect(null);
    setDragState(null);
    setDragPreviewRects({});
    setPlacementPreviewPoint(null);
    setDuplicateSourceDetectionId(null);
    resetSpacePan();
    resetBulkRetag();
  }, [resetBulkRetag, resetSpacePan, revisionId, viewport.activePage]);

  useOutsidePointerDown({
    enabled: selectedDetectionIds.length > 0,
    protectedSelector: [
      "[data-document-tag-detection]",
      "[data-document-tag-editor-ui]",
      "[data-document-tag-context-menu]",
      '[data-slot="select-content"]',
      '[data-slot="alert-dialog-overlay"]',
      '[data-slot="alert-dialog-content"]',
    ].join(", "),
    onOutside: () => {
      setSelectedDetectionIds([]);
      setCreateDraftRect(null);
      setContextMenuPosition(null);
    },
  });

  useEffect(() => {
    if (editMode) {
      return;
    }
    setCreateMode(false);
    setCreateDraftRect(null);
    setDrawingRect(null);
    setDragState(null);
    setDragPreviewRects({});
    setSelectedDetectionIds([]);
    setPlacementPreviewPoint(null);
    setDuplicateSourceDetectionId(null);
    resetSpacePan();
    resetBulkRetag();
  }, [editMode, resetBulkRetag, resetSpacePan]);

  useEffect(() => {
    const availableDetectionIds = new Set(allDetections.map((detection) => String(detection._id)));
    setSelectedDetectionIds((current) => current.filter((detectionId) => availableDetectionIds.has(detectionId)));
  }, [allDetections]);

  useEffect(() => {
    if (!editMode) {
      lastPointerPagePointRef.current = null;
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const pagePoint = getPagePointFromClientPosition(
        event.clientX,
        event.clientY
      );
      if (!hasPlacementPreviewTemplate) {
        lastPointerPagePointRef.current = pagePoint;
        setPlacementPreviewPoint(null);
        return;
      }
      if (isPlacementPreviewSuppressedTarget(event.target)) {
        lastPointerPagePointRef.current = null;
        setPlacementPreviewPoint(null);
        return;
      }
      const overlayBounds = overlayBoundsRef.current?.getBoundingClientRect();
      if (pagePoint && overlayBounds) {
        const previewPageRect = centerRectAtPoint(placementPreviewBboxRef.current, pagePoint);
        const previewScreenRect = viewport.transforms.pageRectToScreenRect(previewPageRect);
        if (
          isPlacementPreviewOverSuppressedUi({
            left: overlayBounds.left + previewScreenRect.x,
            top: overlayBounds.top + previewScreenRect.y,
            width: previewScreenRect.width,
            height: previewScreenRect.height,
          })
        ) {
          lastPointerPagePointRef.current = null;
          setPlacementPreviewPoint(null);
          return;
        }
      }
      lastPointerPagePointRef.current = pagePoint;
      setPlacementPreviewPoint(pagePoint);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [editMode, getPagePointFromClientPosition, hasPlacementPreviewTemplate, viewport.transforms]);

  useEffect(() => {
    if (selectedDetections.length > 1) {
      const selectedOptions = selectedDetections.map(
        (detection) => findTagOptionForDetection(tagOptions, detection)?.id ?? null
      );
      const firstOptionId = selectedOptions[0] ?? null;
      const sharedOptionId = selectedOptions.every((optionId) => optionId === firstOptionId)
        ? firstOptionId
        : null;
      setDraftText("");
      setDraftClassification("unknown");
      setDraftTagOptionId(sharedOptionId);
      return;
    }

    if (selectedDetection) {
      setDraftText(selectedDetection.rawText);
      setDraftClassification(selectedDetection.classification);
      setDraftTagOptionId(findTagOptionForDetection(tagOptions, selectedDetection)?.id ?? null);
      return;
    }
    if (!isCreateDraftActive) {
      setDraftText("");
      setDraftClassification("unknown");
      setDraftTagOptionId(null);
    }
  }, [isCreateDraftActive, selectedDetection, selectedDetections, tagOptions]);

  useEffect(() => {
    if (placementMode !== "duplicate") {
      setDuplicateSourceDetectionId(null);
    }
    if (!placementMode) {
      setPlacementPreviewPoint(null);
    }
  }, [placementMode]);

  useEffect(() => {
    if (!drawingRect && !dragState) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (drawingRect) {
        const pagePoint = getPagePointFromClientPosition(event.clientX, event.clientY);
        if (!pagePoint) {
          return;
        }
        setDrawingRect((current) =>
          current
            ? {
                ...current,
                currentPoint: pagePoint,
              }
            : current
        );
        return;
      }

      if (dragState) {
        const pagePoint = getPagePointFromClientPosition(event.clientX, event.clientY);
        if (!pagePoint) {
          return;
        }
        const primaryRect = dragState.initialRects[String(dragState.detectionId)];
        if (!primaryRect) {
          return;
        }
        const dragRects = Object.values(dragState.initialRects);
        const bounds = getDragDeltaBounds(dragRects);
        const deltaX = clamp(pagePoint.x - dragState.pointerOffsetX - primaryRect.x, bounds.minX, bounds.maxX);
        const deltaY = clamp(pagePoint.y - dragState.pointerOffsetY - primaryRect.y, bounds.minY, bounds.maxY);
        setDragPreviewRects((current) => {
          const next = { ...current };
          for (const [detectionId, rect] of Object.entries(dragState.initialRects)) {
            next[detectionId] = {
              x: rect.x + deltaX,
              y: rect.y + deltaY,
              width: rect.width,
              height: rect.height,
            };
          }
          return next;
        });
      }
    };

    const handleMouseUp = () => {
      if (drawingRect) {
        const rect = normalizeRect(drawingRect.startPoint, drawingRect.currentPoint);
        setDrawingRect(null);
        if (rect.width < 0.01 || rect.height < 0.01) {
          return;
        }
        setCreateDraftRect(rect);
        setCreateMode(false);
        setSelectedDetectionIds([]);
        return;
      }

      if (dragState) {
        const previewRects = Object.fromEntries(
          Object.keys(dragState.initialRects)
            .map((detectionId) => [detectionId, dragPreviewRects[detectionId]] as const)
            .filter((entry): entry is [string, BaseDetectionRecord["bbox"]] => entry[1] !== undefined)
        );
        const detectionIds = Object.keys(previewRects);
        setDragState(null);
        if (detectionIds.length === 0) {
          return;
        }
        const pendingKey = detectionIds.length === 1 ? detectionIds[0] : "selection";
        setPendingId(pendingKey);
        void Promise.all(
          detectionIds.map((detectionId) =>
            moveTag({
              detectionId: detectionId as Id<"documentIntelligenceDetections">,
              bbox: previewRects[detectionId]!,
            })
          )
        )
          .then(() => recordActionSafely())
          .catch((error) => {
            console.error(error);
            notifyError(error, detectionIds.length === 1 ? "Failed to move tag" : "Failed to move tags");
          })
          .finally(() => {
            setPendingId((current) => (current === pendingKey ? null : current));
            setDragPreviewRects((current) => {
              const next = { ...current };
              for (const detectionId of detectionIds) {
                delete next[detectionId];
              }
              return next;
            });
          });
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragPreviewRects, dragState, drawingRect, getPagePointFromClientPosition, moveTag, recordActionSafely]);

  const liveDraftRect = drawingRect
    ? normalizeRect(drawingRect.startPoint, drawingRect.currentPoint)
    : null;
  const liveDraftScreenRect = liveDraftRect
    ? viewport.transforms.pageRectToScreenRect(liveDraftRect)
    : null;

  const buildClipboardTag = useCallback(
    (detection: TDetection): ClipboardTag => ({
      sourceDetectionId: detection._id,
      rawText: detection.rawText,
      classification: detection.classification,
      bbox: detection.bbox,
    }),
    []
  );

  const getDuplicateTargetBbox = useCallback((bbox: BaseDetectionRecord["bbox"], placement: DuplicatePlacement) => {
    if (placement === "offset") {
      return offsetRect(bbox);
    }

    const pointerPagePoint = lastPointerPagePointRef.current;
    return pointerPagePoint ? centerRectAtPoint(bbox, pointerPagePoint) : offsetRect(bbox);
  }, []);

  const createTagFromClipboard = useCallback(
    async (clipboardTag: ClipboardTag, placement: DuplicatePlacement = "cursor") => {
      const targetBbox = getDuplicateTargetBbox(clipboardTag.bbox, placement);
      const pendingKey = clipboardTag.sourceDetectionId
        ? String(clipboardTag.sourceDetectionId)
        : "paste";

      try {
        setPendingId(pendingKey);
        let createdId: Id<"documentIntelligenceDetections">;

        if (clipboardTag.sourceDetectionId) {
          try {
            createdId = await duplicateTag({
              detectionId: clipboardTag.sourceDetectionId,
              revisionId,
              pageNumber: viewport.activePage,
              bbox: targetBbox,
            });
          } catch {
            createdId = await createManualTag({
              revisionId,
              pageNumber: viewport.activePage,
              rawText: clipboardTag.rawText,
              classification: clipboardTag.classification,
              bbox: targetBbox,
            });
          }
        } else {
          createdId = await createManualTag({
            revisionId,
            pageNumber: viewport.activePage,
            rawText: clipboardTag.rawText,
            classification: clipboardTag.classification,
            bbox: targetBbox,
          });
        }

        setSelectedDetectionIds([String(createdId)]);
        await recordActionSafely();
        toast.success("Tag duplicated");
      } catch (error) {
        console.error(error);
        notifyError(error, "Failed to duplicate tag");
      } finally {
        setPendingId(null);
      }
    },
    [createManualTag, duplicateTag, getDuplicateTargetBbox, recordActionSafely, revisionId, viewport.activePage]
  );

  const createTagFromOption = useCallback(
    async (option: DocumentTagOption, point: { x: number; y: number }) => {
      const template = getOptionTemplate({ option, allDetections });
      const targetBbox = centerRectAtPoint(template.bbox, point);

      try {
        setPendingId(`add-${option.id}`);
        const createdId = await createManualTag({
          revisionId,
          pageNumber: viewport.activePage,
          rawText: template.rawText,
          classification: template.classification,
          bbox: targetBbox,
        });
        setSelectedDetectionIds([String(createdId)]);
        await recordActionSafely();
        toast.success("Tag created");
      } catch (error) {
        console.error(error);
        notifyError(error, "Failed to create tag");
      } finally {
        setPendingId(null);
      }
    },
    [allDetections, createManualTag, recordActionSafely, revisionId, viewport.activePage]
  );

  const handleSave = async () => {
    const draftOption = draftTagOptionId
      ? tagOptions.find((option) => option.id === draftTagOptionId) ?? null
      : null;
    const trimmedText = (draftOption?.rawText ?? draftText).trim();
    const resolvedClassification = draftOption
      ? getOptionTemplate({ option: draftOption, allDetections }).classification
      : draftClassification;
    if (!trimmedText) {
      toast.error("Tag text is required");
      return;
    }
    try {
      if (createDraftRect) {
        setPendingId("create");
        const createdId = await createManualTag({
          revisionId,
          pageNumber: viewport.activePage,
          rawText: trimmedText,
          classification: resolvedClassification,
          bbox: createDraftRect,
        });
        setCreateDraftRect(null);
        setSelectedDetectionIds([String(createdId)]);
        await recordActionSafely();
        toast.success("Tag created");
        return;
      }
      if (selectedDetections.length === 0) {
        return;
      }
      const pendingKey = selectedDetections.length === 1 ? String(selectedDetections[0]!._id) : "selection";
      setPendingId(pendingKey);
      await Promise.all(
        selectedDetections.map((detection) =>
          updateTag({
            detectionId: detection._id,
            rawText: trimmedText,
            classification: draftOption
              ? resolvedClassification
              : detection.classification,
          })
        )
      );
      await recordActionSafely();
      toast.success(selectedDetections.length === 1 ? "Tag updated" : `${selectedDetections.length} tags updated`);
    } catch (error) {
      console.error(error);
      notifyError(error, createDraftRect ? "Failed to create tag" : "Failed to update tag");
    } finally {
      setPendingId(null);
    }
  };

  const handleDuplicate = async (placement: DuplicatePlacement = "cursor") => {
    if (!selectedDetection) return;
    await createTagFromClipboard(buildClipboardTag(selectedDetection), placement);
  };

  const handleHideSelected = async () => {
    if (selectedActiveDetections.length === 0) {
      return;
    }
    try {
      const pendingKey = selectedActiveDetections.length === 1 ? String(selectedActiveDetections[0]!._id) : "selection";
      setPendingId(pendingKey);
      await Promise.all(
        selectedActiveDetections.map((detection) => archiveTag({ detectionId: detection._id }))
      );
      await recordActionSafely();
      toast.success(selectedActiveDetections.length === 1 ? "Tag hidden" : `${selectedActiveDetections.length} tags hidden`);
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to hide tags");
    } finally {
      setPendingId(null);
    }
  };

  const handleUnhideSelected = async () => {
    if (selectedArchivedDetections.length === 0) {
      return;
    }
    try {
      const pendingKey = selectedArchivedDetections.length === 1 ? String(selectedArchivedDetections[0]!._id) : "selection";
      setPendingId(pendingKey);
      await Promise.all(
        selectedArchivedDetections.map((detection) => restoreTag({ detectionId: detection._id }))
      );
      await recordActionSafely();
      toast.success(selectedArchivedDetections.length === 1 ? "Tag unhidden" : `${selectedArchivedDetections.length} tags unhidden`);
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to unhide tags");
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async () => {
    if (selectedDeletableDetections.length === 0 || selectedDeletableDetections.length !== selectedDetections.length) {
      return;
    }
    try {
      const pendingKey = selectedDeletableDetections.length === 1 ? String(selectedDeletableDetections[0]!._id) : "selection";
      setPendingId(pendingKey);
      await Promise.all(
        selectedDeletableDetections.map((detection) => deleteTag({ detectionId: detection._id }))
      );
      await recordActionSafely();
      const deletedIds = new Set(selectedDeletableDetections.map((detection) => String(detection._id)));
      setSelectedDetectionIds((current) => current.filter((detectionId) => !deletedIds.has(detectionId)));
      setCreateDraftRect(null);
      toast.success(selectedDeletableDetections.length === 1 ? "Tag deleted" : `${selectedDeletableDetections.length} tags deleted`);
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to delete tag");
    } finally {
      setPendingId(null);
    }
  };

  const handleCopy = useCallback(() => {
    if (!selectedDetection) return;
    clipboardRef.current = buildClipboardTag(selectedDetection);
    toast.success("Tag copied");
  }, [buildClipboardTag, selectedDetection]);

  const handlePaste = useCallback(() => {
    if (!clipboardRef.current) return;
    void createTagFromClipboard(clipboardRef.current);
  }, [createTagFromClipboard]);

  useDocumentTagHotkeys({
    editMode,
    hasSelectedDetection: selectedDetection !== null,
    onCopy: handleCopy,
    onPaste: handlePaste,
    onDuplicate: () => {
      void handleDuplicate("cursor");
    },
  });

  const handleDetectionDragStart = useCallback(
    (detection: TDetection, archived: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
      if (placementMode || !editMode || archived || detection.createdByUserId === undefined) {
        return;
      }
      const pagePoint = getPagePointFromClientPosition(event.clientX, event.clientY);
      if (!pagePoint) {
        return;
      }
      const detectionId = String(detection._id);
      const canDragSelection =
        selectedDetectionIdSet.has(detectionId) &&
        selectedDetections.length > 1 &&
        selectedDetections.every(
          (selected) => !isArchivedDetection(selected) && selected.createdByUserId !== undefined
        );
      const dragDetections = canDragSelection ? selectedDetections : [detection];
      setCreateDraftRect(null);
      if (!canDragSelection) {
        setSelectedDetectionIds([detectionId]);
      }
      setDragState({
        detectionId: detection._id,
        pointerOffsetX: pagePoint.x - detection.bbox.x,
        pointerOffsetY: pagePoint.y - detection.bbox.y,
        width: detection.bbox.width,
        height: detection.bbox.height,
        initialRects: Object.fromEntries(
          dragDetections.map((dragDetection) => [String(dragDetection._id), dragDetection.bbox])
        ),
      });
    },
    [editMode, getPagePointFromClientPosition, isArchivedDetection, placementMode, selectedDetectionIdSet, selectedDetections]
  );

  const highlightedMarkerPosition = useHighlightedMarkerPosition({
    allDetections,
    highlightedDetectionId,
    pageRectToScreenRect: viewport.transforms.pageRectToScreenRect,
  });

  const selectedAddTagOption =
    selectedAddTagOptionId
      ? tagOptions.find((option) => option.id === selectedAddTagOptionId) ?? null
      : tagOptions[0] ?? null;
  const duplicateSourceDetection = duplicateSourceDetectionId
    ? allDetections.find((detection) => String(detection._id) === duplicateSourceDetectionId) ?? null
    : null;
  const previewTemplate =
    placementMode === "add" && selectedAddTagOption
      ? getOptionTemplate({ option: selectedAddTagOption, allDetections })
      : placementMode === "duplicate" && duplicateSourceDetection
        ? {
            rawText: duplicateSourceDetection.rawText,
            classification: duplicateSourceDetection.classification,
            bbox: duplicateSourceDetection.bbox,
          }
        : null;
  placementPreviewBboxRef.current = previewTemplate?.bbox ?? DEFAULT_TAG_BBOX;
  const previewStyle =
    placementMode === "add" && selectedAddTagOption
      ? {
          color: selectedAddTagOption.color ?? "hsl(var(--primary))",
          fillColor: selectedAddTagOption.color ? `${selectedAddTagOption.color}2E` : undefined,
        }
      : duplicateSourceDetection
        ? getStyle(duplicateSourceDetection)
        : null;
  const placementPreviewRect =
    placementPreviewPoint && previewTemplate
      ? centerRectAtPoint(previewTemplate.bbox, placementPreviewPoint)
      : null;
  const placementPreviewScreenRect = placementPreviewRect
    ? viewport.transforms.pageRectToScreenRect(placementPreviewRect)
    : null;
  const placementPreviewScreenPoint = placementPreviewPoint
    ? viewport.transforms.pageRectToScreenRect({
        x: placementPreviewPoint.x,
        y: placementPreviewPoint.y,
        width: 0,
        height: 0,
      })
    : null;

  const shouldRenderPanel = shouldRenderTagEditorPanel({
    hidePanelWhenNotEditing,
    editMode,
    showPanelOnlyWhenSelection,
    hasPanelSelection: createDraftRect !== null || selectedDetection !== null,
  });
  const selectedDetectionAction =
    selectedDetection && getSelectedDetectionAction
      ? getSelectedDetectionAction(selectedDetection)
      : null;

  return (
    <>
      <div ref={overlayBoundsRef} className="pointer-events-none absolute inset-0 z-30">
        <DetectionList
          activeDetections={activeDetections}
          archivedDetections={archivedDetections}
          getStyle={getStyle}
          showInlineLabel={showInlineLabel}
          secondaryLabel={secondaryLabel}
          pageRectToScreenRect={viewport.transforms.pageRectToScreenRect}
          dragPreviewRects={dragPreviewRects}
          pendingId={pendingId}
          isSpacePanActive={isSpacePanActive}
          selectedDetectionIds={selectedDetectionIdSet}
          duplicateSourceDetectionId={duplicateSourceDetectionId}
          highlightedDetectionId={highlightedDetectionId}
          onSelectDetection={(detectionId, additive) => {
            setContextMenuPosition(null);
            if (placementMode === "duplicate") {
              setDuplicateSourceDetectionId(detectionId);
              setSelectedDetectionIds([detectionId]);
              setCreateDraftRect(null);
              return;
            }
            setCreateDraftRect(null);
            setSelectedDetectionIds((current) => getSelectionUpdate(current, detectionId, additive));
          }}
          onOpenContextMenu={(detectionId, event) => {
            if (!canEdit) return;
            setCreateDraftRect(null);
            setSelectedDetectionIds([detectionId]);
            const bounds = overlayBoundsRef.current?.getBoundingClientRect();
            setContextMenuPosition({
              x: bounds ? event.clientX - bounds.left : event.clientX,
              y: bounds ? event.clientY - bounds.top : event.clientY,
            });
          }}
          onStartSpacePan={startSpacePan}
          onStartDrag={handleDetectionDragStart}
        />
        {highlightedMarkerPosition ? (
          <div
            className="absolute z-20 -translate-x-1/2 -translate-y-1/2"
            style={{
              left: highlightedMarkerPosition.x,
              top: highlightedMarkerPosition.y,
            }}
          >
            <div className="relative flex items-center justify-center">
              <div className="absolute size-12 rounded-full border-2 border-amber-300/90 bg-amber-300/10 animate-ping" />
              <div className="absolute size-8 rounded-full border-2 border-amber-300 shadow-[0_0_20px_rgba(252,211,77,0.55)]" />
              <div className="size-3 rounded-full border border-background bg-amber-300 shadow-[0_0_12px_rgba(252,211,77,0.85)]" />
            </div>
            <div className="absolute left-1/2 top-full mt-3 -translate-x-1/2 whitespace-nowrap rounded-full border border-amber-300/70 bg-background/95 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-500 shadow-sm">
              {highlightedMarkerPosition.label}
            </div>
          </div>
        ) : null}
        {liveDraftScreenRect ? (
          <div
            className="absolute rounded-[10px] border-2 border-dashed border-primary bg-primary/10"
            style={getAbsoluteRectStyle(liveDraftScreenRect)}
          />
        ) : null}
      </div>

      {editMode ? (
        <div
          data-testid="document-tag-editor-surface"
          className={cn(
            "absolute inset-0 z-20",
            isSpacePanActive ? "cursor-grab" : placementMode ? "cursor-default" : "cursor-crosshair"
          )}
          onPointerMove={(event) => {
            if (!placementMode || isSpacePanActive) {
              return;
            }
            const pagePoint = getPagePointFromClientPosition(event.clientX, event.clientY);
            lastPointerPagePointRef.current = pagePoint;
            setPlacementPreviewPoint(pagePoint);
          }}
          onMouseDown={(event) => {
            if (isSpacePanActive) {
              startSpacePan(event);
              return;
            }
            if (placementMode) {
              return;
            }
            startCreateRect({
              allowCreate,
              editMode,
              createMode,
              event,
              getPagePointFromClientPosition,
              setDrawingRect,
            });
          }}
          onClick={(event) => {
            if (consumeSuppressedClick() || isSpacePanActive || spacePanStart) {
              return;
            }
            if (placementMode === "add") {
              const point = getPagePointFromClientPosition(event.clientX, event.clientY);
              if (point && selectedAddTagOption) {
                void createTagFromOption(selectedAddTagOption, point);
              }
              return;
            }

            if (placementMode === "duplicate") {
              const point = getPagePointFromClientPosition(event.clientX, event.clientY);
              if (point && duplicateSourceDetection) {
                lastPointerPagePointRef.current = point;
                void createTagFromClipboard(buildClipboardTag(duplicateSourceDetection), "cursor");
              }
              return;
            }

            if (!createMode) {
              setSelectedDetectionIds([]);
              setCreateDraftRect(null);
            }
          }}
        />
      ) : null}

      {previewTemplate ? (
        <div className="pointer-events-none absolute inset-0 z-[45]">
          {placementPreviewScreenRect ? (
            <div
              data-testid="document-tag-placement-preview"
              className="absolute overflow-hidden rounded-[10px] border-2 border-dashed shadow-[0_0_0_1px_rgba(15,23,42,0.18),0_10px_28px_rgba(15,23,42,0.28)]"
              style={{
                ...getAbsoluteRectStyle(placementPreviewScreenRect),
                borderColor: previewStyle?.color,
                backgroundColor: previewStyle?.fillColor,
              }}
            />
          ) : null}
          {placementPreviewScreenPoint ? (
            <div
              data-testid="document-tag-placement-preview-label"
              className="absolute translate-x-3 translate-y-3 rounded-full border-2 bg-background/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider shadow-xl backdrop-blur"
              style={{
                left: placementPreviewScreenPoint.x,
                top: placementPreviewScreenPoint.y,
                borderColor: previewStyle?.color,
                color: previewStyle?.color,
              }}
            >
              {previewTemplate.rawText}
            </div>
          ) : null}
        </div>
      ) : null}

      <DocumentTagEditorPanel
        panelClassName={panelClassName}
        shouldRenderPanel={canEdit && shouldRenderPanel}
        showEditToggle={showEditToggle}
        editMode={editMode}
        allowCreate={allowCreate}
        createMode={createMode}
        pendingId={pendingId}
        createDraftRect={createDraftRect}
        selectedDetection={selectedDetection}
        selectedDetectionCount={selectedDetections.length}
        selectedActiveDetectionCount={selectedActiveDetections.length}
        selectedArchivedDetectionCount={selectedArchivedDetections.length}
        selectedDeletableDetectionCount={selectedDeletableDetections.length}
        selectedDetectionWasCreatedByUser={selectedDetectionWasCreatedByUser}
        canSaveSelection={
          createDraftRect !== null ||
          selectedDetections.length <= 1 ||
          draftTagOptionId !== null ||
          (tagOptions.length === 0 && draftText.trim().length > 0)
        }
        draftText={draftText}
        tagOptions={tagOptions}
        draftTagOptionId={draftTagOptionId}
        bulkTargetOptionId={bulkTargetOptionId}
        showBulkRetagControls={showBulkRetagControls}
        onDraftTextChange={setDraftText}
        onDraftTagOptionChange={(optionId) => {
          setDraftTagOptionId(optionId);
          const option = tagOptions.find((tagOption) => tagOption.id === optionId);
          if (!option) {
            return;
          }
          const template = getOptionTemplate({ option, allDetections });
          setDraftText(template.rawText);
          setDraftClassification(template.classification);
        }}
        onBulkTargetOptionChange={setBulkTargetOptionId}
        onToggleBulkRetagControls={toggleBulkRetagControls}
        onRequestBulkRetag={requestBulkRetag}
        onToggleEditMode={() => {
          setEditMode((current) => !current);
          setCreateMode(false);
          setCreateDraftRect(null);
        }}
        onToggleCreateMode={() => {
          setCreateMode((current) => !current);
          setCreateDraftRect(null);
          setSelectedDetectionIds([]);
        }}
        onSave={() => {
          void handleSave();
        }}
        onHideSelected={() => {
          void handleHideSelected();
        }}
        onUnhideSelected={() => {
          void handleUnhideSelected();
        }}
        onDelete={() => {
          void handleDelete();
        }}
        onClearSelection={() => {
          setCreateDraftRect(null);
          setSelectedDetectionIds([]);
        }}
        selectedDetectionAction={selectedDetectionAction}
      />
      {contextMenuPosition && selectedDetection ? (
        <DocumentTagContextMenu
          x={contextMenuPosition.x}
          y={contextMenuPosition.y}
          pending={pendingId !== null}
          onClose={() => setContextMenuPosition(null)}
          visibilityLabel={isArchivedDetection(selectedDetection) ? "Unhide" : "Hide"}
          onToggleVisibility={() => {
            if (isArchivedDetection(selectedDetection)) {
              void handleUnhideSelected();
              return;
            }
            void handleHideSelected();
          }}
          selectedDetectionAction={selectedDetectionAction}
        />
      ) : null}
      <ConfirmationDialog
        open={pendingBulkRetag !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingBulkRetag(null);
          }
        }}
        title="Change all matching tags?"
        description={
          pendingBulkRetag
            ? `This will change all instances of "${pendingBulkRetag.source.rawText}" to "${pendingBulkRetag.target.rawText}" in the current plan document revision, including hidden tags.`
            : "This will change matching tags in the current plan document revision."
        }
        confirmLabel="Change All"
        disabled={pendingId === "bulk-retag"}
        onConfirm={confirmBulkRetag}
      />
    </>
  );
}
